"use strict";
// Copyright (c) 2026 The Bitcoin developers
// Distributed under the MIT software license, see the accompanying
// file COPYING or http://www.opensource.org/licenses/mit-license.php.
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.toFormattedFiatNotification = exports.toFormattedTokenQty = exports.toFormattedXec = exports.toFormattedCompactAmount = exports.decimalizedTokenQtyToLocaleFormat = void 0;
const app_1 = __importDefault(require("./constants/app"));
const amounts_1 = require("./amounts");
const decimalizedTokenQtyToLocaleFormat = (decimalizedTokenQty, userLocale) => {
    const localeDecimalSymbol = Number(1).toLocaleString(userLocale, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
    })[1];
    const localeThousand = Number(1000).toLocaleString(userLocale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });
    const LOCALE_HAS_THOUSANDS_LENGTH = 5;
    const hasThousandSeparator = localeThousand.length === LOCALE_HAS_THOUSANDS_LENGTH;
    const localeThousandsSymbol = hasThousandSeparator ? localeThousand[1] : '';
    const beforeAndAfterDecimalStrings = decimalizedTokenQty.split('.');
    const beforeDecimalString = beforeAndAfterDecimalStrings[0];
    let localeTokenString = beforeDecimalString.replace(/\B(?=(\d{3})+(?!\d))/g, localeThousandsSymbol);
    if (beforeAndAfterDecimalStrings.length > 1) {
        localeTokenString = `${localeTokenString}${localeDecimalSymbol}${beforeAndAfterDecimalStrings[1]}`;
    }
    return localeTokenString;
};
exports.decimalizedTokenQtyToLocaleFormat = decimalizedTokenQtyToLocaleFormat;
const TRILLION = 1e12;
const BILLION = 1e9;
const MILLION = 1e6;
const THOUSAND = 1e3;
/**
 * Compact human-readable amount for notifications (e.g. 1.5k, 2.3M, 1.1B).
 * Prefers readability over full precision.
 */
const toFormattedCompactAmount = (amount, userLocale, minimumFractionDigitsBelowThousand = 0) => {
    if (!Number.isFinite(amount)) {
        return String(amount);
    }
    const amountRaw = amount;
    let scaled = amountRaw;
    let units = 'T';
    let minimumFractionDigits = 0;
    if (scaled >= TRILLION) {
        scaled = scaled / TRILLION;
    }
    else if (scaled >= BILLION) {
        scaled = scaled / BILLION;
        units = 'B';
    }
    else if (scaled >= MILLION) {
        scaled = scaled / MILLION;
        units = 'M';
    }
    else if (scaled >= THOUSAND) {
        scaled = scaled / THOUSAND;
        units = 'k';
    }
    else {
        units = '';
        minimumFractionDigits = minimumFractionDigitsBelowThousand;
    }
    // Avoid "1,000k" when rounding would hit the next unit
    if (units !== '' && Number(scaled.toFixed(2)) >= 1000) {
        if (units === 'k') {
            scaled = amountRaw / MILLION;
            units = 'M';
        }
        else if (units === 'M') {
            scaled = amountRaw / BILLION;
            units = 'B';
        }
        else if (units === 'B') {
            scaled = amountRaw / TRILLION;
            units = 'T';
        }
    }
    return `${scaled.toLocaleString(userLocale, {
        maximumFractionDigits: 2,
        minimumFractionDigits,
    })}${units}`;
};
exports.toFormattedCompactAmount = toFormattedCompactAmount;
/** Compact XEC amount from satoshis (e.g. 42.00, 625.01k, 1.5M). */
const toFormattedXec = (satoshis, userLocale) => (0, exports.toFormattedCompactAmount)((0, amounts_1.toXec)(satoshis), userLocale, app_1.default.cashDecimals);
exports.toFormattedXec = toFormattedXec;
/**
 * Compact token quantity for notifications.
 * Small amounts keep full decimalized locale form; large amounts use k/M/B/T.
 */
const toFormattedTokenQty = (decimalizedTokenQty, userLocale) => {
    const amount = Number(decimalizedTokenQty);
    if (!Number.isFinite(amount) || Math.abs(amount) < THOUSAND) {
        return (0, exports.decimalizedTokenQtyToLocaleFormat)(decimalizedTokenQty, userLocale);
    }
    return (0, exports.toFormattedCompactAmount)(amount, userLocale);
};
exports.toFormattedTokenQty = toFormattedTokenQty;
/** Compact fiat for notification bodies when amount is large. */
const toFormattedFiatNotification = (fiatAmount, userLocale, selectedFiatTicker) => {
    if (Math.abs(fiatAmount) >= THOUSAND) {
        return `${(0, exports.toFormattedCompactAmount)(fiatAmount, userLocale)} ${selectedFiatTicker}`;
    }
    return `${new Intl.NumberFormat(userLocale, {
        style: 'currency',
        currency: selectedFiatTicker,
        minimumFractionDigits: app_1.default.cashDecimals,
        maximumFractionDigits: app_1.default.cashDecimals,
    }).format(fiatAmount)} ${selectedFiatTicker}`;
};
exports.toFormattedFiatNotification = toFormattedFiatNotification;
//# sourceMappingURL=formatting.js.map